// frontend/src/components/legal/PrivacyPolicy.js
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, Globe, UserCheck, AlertTriangle } from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-surface-page">
      {/* Header */}
      <div className="bg-surface-card border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center text-ink-muted hover:text-ink-soft transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </Link>
            <div className="flex items-center text-ink-muted text-sm">
              <Shield className="w-4 h-4 mr-1" />
              Last Updated: January 2024
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-surface-card rounded-card shadow-elevation-1 p-8 md:p-12">
          {/* Title Section */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Shield className="w-12 h-12 text-success" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-ink-soft mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-ink-muted">
              Your privacy is important to us
            </p>
          </div>

          {/* GDPR & CCPA Notice */}
          <div className="bg-success-tint border border-success-border rounded-card p-4 mb-8 max-w-[34rem] mx-auto">
            <div className="flex">
              <UserCheck className="w-5 h-5 text-success mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h2 className="text-lg font-semibold text-success mb-2">
                  GDPR & CCPA Compliant
                </h2>
                <p className="text-success">
                  The CROW is committed to protecting your privacy and complying with data protection 
                  regulations including GDPR (General Data Protection Regulation) and CCPA (California 
                  Consumer Privacy Act).
                </p>
              </div>
            </div>
          </div>

          {/* Privacy Content */}
          <div className="prose prose-gray max-w-[34rem] mx-auto">
            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-success mr-3">1.</span>
                Introduction
              </h2>
              <p className="text-body text-ink-soft mb-4">
                The CROW ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our 
                automotive maintenance and shop management platform and related services (the "Service").
              </p>
              <p className="text-body text-ink-soft">
                By using the Service, you consent to the data practices described in this Privacy Policy. 
                If you do not agree with our policies and practices, please do not use our Service.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <Database className="w-6 h-6 text-success mr-3" />
                2. Information We Collect
              </h2>
              
              <h3 className="text-section-title text-ink mb-3">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li><strong>Account Information:</strong> Name, email address, password, user type (mechanic/consumer)</li>
                <li><strong>Profile Information:</strong> Business name, phone number, address (optional)</li>
                <li><strong>Vehicle Information:</strong> VIN, make, model, year, mileage</li>
                <li><strong>Payment Information:</strong> Credit card details (processed securely through Stripe)</li>
                <li><strong>Communication Data:</strong> Support tickets, feedback, survey responses</li>
              </ul>

              <h3 className="text-section-title text-ink mb-3">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li><strong>Usage Data:</strong> Features accessed and pages viewed</li>
                <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
                <li><strong>Cookies:</strong> Session cookies, preference cookies, analytics cookies</li>
                <li><strong>Log Data:</strong> Access times, pages viewed, errors encountered</li>
              </ul>

              <h3 className="text-section-title text-ink mb-3">2.3 Information from Third Parties</h3>
              <ul className="list-disc pl-6 text-body text-ink-soft space-y-2">
                <li><strong>Vehicle Data Providers:</strong> VIN decode information, specifications</li>
                <li><strong>Payment Processors:</strong> Transaction confirmations, subscription status</li>
                <li><strong>Analytics Services:</strong> Usage patterns, demographic information</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <Eye className="w-6 h-6 text-success mr-3" />
                3. How We Use Your Information
              </h2>
              <p className="text-body text-ink-soft mb-4">We use the information we collect to:</p>
              
              <h3 className="text-section-title text-ink mb-3">3.1 Provide Services</h3>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li>Manage your vehicle profiles and maintenance records</li>
                <li>Process payments and manage subscriptions</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Send service-related notifications</li>
              </ul>

              <h3 className="text-section-title text-ink mb-3">3.2 Improve Our Platform</h3>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li>Analyze usage patterns to improve features</li>
                <li>Develop new services and functionality</li>
                <li>Conduct research and analytics</li>
                <li>Customize your experience based on preferences</li>
              </ul>

              <h3 className="text-section-title text-ink mb-3">3.3 Marketing and Communications</h3>
              <ul className="list-disc pl-6 text-body text-ink-soft space-y-2">
                <li>Send promotional emails (with your consent)</li>
                <li>Announce new features or services</li>
                <li>Provide tips and automotive insights</li>
                <li>Conduct surveys and gather feedback</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <Globe className="w-6 h-6 text-success mr-3" />
                4. Information Sharing and Disclosure
              </h2>
              <p className="text-body text-ink-soft mb-4">
                We do not sell, trade, or rent your personal information. We may share your information in 
                the following situations:
              </p>

              <h3 className="text-section-title text-ink mb-3">4.1 Service Providers</h3>
              <p className="text-body text-ink-soft mb-4">
                We share information with third-party service providers who help us operate our platform:
              </p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li><strong>Stripe:</strong> Payment processing</li>
                <li><strong>AWS:</strong> Cloud hosting and storage</li>
                <li><strong>SendGrid:</strong> Email delivery</li>
                <li><strong>Google Analytics:</strong> Usage analytics</li>
              </ul>

              <h3 className="text-section-title text-ink mb-3">4.2 Legal Requirements</h3>
              <p className="text-body text-ink-soft mb-4">
                We may disclose your information if required by law or in response to valid requests by 
                public authorities (e.g., court orders, subpoenas).
              </p>

              <h3 className="text-section-title text-ink mb-3">4.3 Business Transfers</h3>
              <p className="text-body text-ink-soft">
                If we are involved in a merger, acquisition, or sale of assets, your information may be 
                transferred as part of that transaction.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <Lock className="w-6 h-6 text-success mr-3" />
                5. Data Security
              </h2>
              <p className="text-body text-ink-soft mb-4">
                We implement appropriate technical and organizational security measures to protect your 
                personal information:
              </p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure password hashing (bcrypt)</li>
                <li>Regular security audits and updates</li>
                <li>Limited access to personal information</li>
                <li>PCI DSS compliance for payment processing</li>
                <li>SSL/TLS certificates for secure connections</li>
              </ul>
              <div className="bg-warning-tint border border-warning-border rounded-card p-4">
                <p className="text-body text-ink-soft">
                  While we strive to protect your information, no method of transmission over the Internet 
                  or electronic storage is 100% secure. We cannot guarantee absolute security.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <UserCheck className="w-6 h-6 text-success mr-3" />
                6. Your Privacy Rights
              </h2>
              
              <h3 className="text-section-title text-ink mb-3">6.1 Access and Control</h3>
              <p className="text-body text-ink-soft mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Restriction:</strong> Limit how we use your information</li>
                <li><strong>Objection:</strong> Object to certain processing activities</li>
              </ul>

              <h3 className="text-section-title text-ink mb-3">6.2 Opt-Out Rights</h3>
              <p className="text-body text-ink-soft mb-4">You can opt out of:</p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li>Marketing emails (via unsubscribe link)</li>
                <li>Analytics cookies (via cookie settings)</li>
                <li>Targeted advertising</li>
                <li>Data sharing with partners</li>
              </ul>

              <h3 className="text-section-title text-ink mb-3">6.3 California Privacy Rights (CCPA)</h3>
              <p className="text-body text-ink-soft mb-4">
                California residents have additional rights under the CCPA:
              </p>
              <ul className="list-disc pl-6 text-body text-ink-soft space-y-2">
                <li>Right to know what personal information is collected</li>
                <li>Right to delete personal information</li>
                <li>Right to opt-out of the sale of personal information</li>
                <li>Right to non-discrimination for exercising privacy rights</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-success mr-3">7.</span>
                Cookies and Tracking
              </h2>
              <p className="text-body text-ink-soft mb-4">
                We use cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li>Maintain your session and authentication</li>
                <li>Remember your preferences and settings</li>
                <li>Analyze usage patterns and trends</li>
                <li>Improve our services and user experience</li>
              </ul>
              <p className="text-body text-ink-soft">
                You can control cookies through your browser settings. Disabling cookies may limit some 
                features of our Service.
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-success mr-3">8.</span>
                Data Retention
              </h2>
              <p className="text-body text-ink-soft mb-4">
                We retain your personal information for as long as necessary to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-body text-ink-soft space-y-2">
                <li>Provide our services to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Maintain business records</li>
              </ul>
              <p className="text-body text-ink-soft">
                When you delete your account, we will delete or anonymize your personal information within 
                30 days, except where retention is required by law.
              </p>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-success mr-3">9.</span>
                Children's Privacy
              </h2>
              <p className="text-body text-ink-soft">
                Our Service is not directed to children under 18. We do not knowingly collect personal 
                information from children. If you believe we have collected information from a child, 
                please contact us immediately.
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-success mr-3">10.</span>
                International Data Transfers
              </h2>
              <p className="text-body text-ink-soft">
                Your information may be transferred to and processed in countries other than your own. 
                We ensure appropriate safeguards are in place to protect your information in accordance 
                with this Privacy Policy.
              </p>
            </section>

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-success mr-3">11.</span>
                Updates to This Policy
              </h2>
              <p className="text-body text-ink-soft">
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last Updated" date. Material 
                changes will be communicated via email or prominent notice on our Service.
              </p>
            </section>

            {/* Section 12 */}
            <section className="mb-8">
              <h2 className="text-section-title text-ink mb-4 flex items-center">
                <span className="text-success mr-3">12.</span>
                Contact Us
              </h2>
              <p className="text-body text-ink-soft mb-4">
                If you have questions about this Privacy Policy or want to exercise your privacy rights, 
                please contact us:
              </p>
              <div className="bg-surface-page rounded-lg p-4">
                <p className="text-body text-ink-soft">
                  <strong>Privacy Officer</strong><br />
                  The CROW<br />
                  Email: hello@getcrowmobile.com<br />
                  Phone: 514-617-5478<br />
                  Address: 8 Strathearn Ave, Unit 24, Brampton, ON L6T 4L9
                </p>
              </div>
              <p className="text-body text-ink-soft mt-4">
                For EU residents, you may also contact your local data protection authority with complaints.
              </p>
            </section>
          </div>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-border-default">
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8">
              <Link 
                to="/terms" 
                className="text-accent hover:text-accent-hover font-medium"
              >
                Terms of Service
              </Link>
              <a 
                href="mailto:hello@getcrowmobile.com" 
                className="text-accent hover:text-accent-hover font-medium"
              >
                Contact Privacy Team
              </a>
              <button 
                onClick={() => window.print()} 
                className="text-accent hover:text-accent-hover font-medium"
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;